"use client";

import { FormField, FormItem, FormLabel, FormMessage } from "./form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/common/Select";
import type { Control, FieldValues, Path } from "react-hook-form";
import Flex from "./Flex";
import { format, getYear } from "date-fns-jalali";
import { format as gFormat, getYear as gGetYear } from "date-fns";
import {
  generateDays,
  generateMonths,
  generateYears,
} from "@/shared/libs/date";
import data from "@/public/data/components/datepicker.json";

interface YearRange {
  start?: number | null;
  end?: number | null;
}
interface DatepickerProps<FormType extends FieldValues> {
  control: Control<FormType>;
  name: Path<FormType>;
  yearRange?: YearRange;
  type?: "gregorian" | "jalalli";
  passengerType?: "adult" | "child" | "infant";
  label?: string;
  withScroll?: boolean;
  service?: TServices;
}

export default function Datepicker<FormType extends FieldValues>({
  control,
  name,
  yearRange,
  type = "jalalli",
  label,
  passengerType,
  withScroll = false,
  service,
}: DatepickerProps<FormType>) {
  // gregorian year
  const { end, start } = yearRange ?? {};

  const currentYear =
    type === "jalalli" ? getYear(new Date()) : gGetYear(new Date());
  const days = generateDays(end ?? currentYear, 1);
  const months = generateMonths();
  const years =
    type === "gregorian"
      ? generateYears(start ?? 1950, end ?? currentYear) // Gregorian for adults
      : passengerType === "child"
        ? generateYears(start ?? 1391, end ?? currentYear) // Jalali range for children (1391-1400)
        : generateYears(start ?? 1300, end ?? currentYear); // Jalali for adults

  const { day, month, year } = data;

  return (
    <Flex className="w-full justify-center">
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem className="w-full relative">
            <FormLabel
              className="text-muted-foreground"
              value={!!field.value}
              variant="dynamic"
            >
              {label}
            </FormLabel>

            <Flex className="w-full h-12">
              <FormField
                control={control}
                name={`${name}.day` as Path<FormType>}
                render={({ field }) => (
                  <FormItem className="basis-1/6 relative">
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className="!h-full rounded-l-none rounded-r-sm border-border shadow-none!">
                        <SelectValue placeholder={day} />
                      </SelectTrigger>
                      <SelectContent className="w-full rounded-sm">
                        {days.map((day) => (
                          <SelectItem
                            key={day}
                            value={String(day)}
                            className="rounded-none!"
                          >
                            {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`${name}.month` as Path<FormType>}
                render={({ field }) => (
                  <FormItem className="basis-3/6 relative">
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className="w-full !h-full rounded-l-none rounded-r-none border-l-0 border-r-0 border-border shadow-none!">
                        <SelectValue placeholder={month} />
                      </SelectTrigger>
                      <SelectContent className="w-full rounded-sm">
                        {months.map((month) => (
                          <SelectItem
                            key={month}
                            value={String(month)}
                            className="rounded-none! text-muted-foreground"
                          >
                            {(type === "jalalli" ? format : gFormat)(
                              new Date(
                                end ?? currentYear,
                                type === "jalalli" ? month + 2 : month - 1,
                              ),
                              "MMMM",
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`${name}.year` as Path<FormType>}
                render={({ field }) => (
                  <FormItem className="basis-2/6 relative">
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className="w-full h-full! rounded-r-none rounded-l-sm border-border shadow-none!">
                        <SelectValue placeholder={year} />
                      </SelectTrigger>
                      <SelectContent className="w-full rounded-sm">
                        {years.map((year) => (
                          <SelectItem
                            key={year}
                            value={String(year)}
                            className="rounded-none! text-muted-foreground"
                          >
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </Flex>
            <FormMessage
              className={`absolute ${
                service === "hotel" ? "-bottom-[33px]!" : "-bottom-[18px]!"
              }  w-full text-xs`}
            />
          </FormItem>
        )}
      />
    </Flex>
  );
}
